import { useTranslation } from 'react-i18next';
import { DeadlineUnit, NameChangeFeeMode } from '@/api/generated/schemas';
import { SegmentedField, TextField, TextareaField } from '@/shared/forms';
import { Alert, Panel } from '@/shared/ui';
import { AIRLINE_NAME_CHANGE_MODES, type AirlineNameChangeMode } from '../airlineFormSchema';
import type { AirlineFormApi } from '../useAirlineForm';

const LABEL_W = 168;

interface AirlineNameChangePanelProps {
  form: AirlineFormApi;
}

/**
 * Sekce „Name changes" záložky Extra services — podmínky, za kterých dopravce umožňuje změnu jména
 * cestujícího. Je to **jeden blok, ne kolekce pravidel**: jedna sazba a jedna lhůta na dopravce.
 * <p>
 * Pole jsou součástí formuláře letecké společnosti a ukládají se s ním (na rozdíl od pravidel
 * zavazadel s vlastními endpointy). Výchozí stav je „nepovoleno"; existence bloku podmínek sama
 * o sobě znamená „ano", takže přepnutí zpět hodnoty rovnou zahodí — server celý blok maže a jinak
 * by se uživateli po návratu vrátila čísla, která už nikde nejsou.
 * </p>
 */
export function AirlineNameChangePanel({ form }: AirlineNameChangePanelProps) {
  const { t } = useTranslation('inventory');

  const nameChangeOptions = AIRLINE_NAME_CHANGE_MODES.map((value) => ({
    value,
    label: t(`airlineNameChangeMode.${value}`),
  }));

  const feeModeOptions = Object.values(NameChangeFeeMode).map((value) => ({
    value,
    label: t(`nameChangeFeeMode.${value}`),
  }));

  const deadlineUnitOptions = Object.values(DeadlineUnit).map((value) => ({
    value,
    label: t(`deadlineUnit.${value}`),
  }));

  return (
    <Panel variant="filled" icon="user" title={t('airline.sections.nameChanges')}>
      <div className="gap-phx-3 flex flex-col">
        <form.Field
          name="nameChanges"
          listeners={{
            onChange: ({ value }) => {
              if (value === 'NotAllowed') {
                form.setFieldValue('nameChangeFeeMode', null);
                form.setFieldValue('nameChangeDeadlineValue', '');
                form.setFieldValue('nameChangeDeadlineUnit', DeadlineUnit.Hours);
                form.setFieldValue('nameChangeOnlineFee', '');
                form.setFieldValue('nameChangeAirportFee', '');
                form.setFieldValue('nameChangeNamelistValue', '');
                form.setFieldValue('nameChangeNamelistUnit', DeadlineUnit.Hours);
                form.setFieldValue('nameChangeNote', '');
              }
            },
          }}
        >
          {(field) => (
            <SegmentedField<AirlineNameChangeMode>
              field={field}
              layout="inline"
              labelWidth={LABEL_W}
              label={t('airline.nameChanges.label')}
              required
              options={nameChangeOptions}
              testId="airline-name-changes"
            />
          )}
        </form.Field>

        <form.Subscribe selector={(state) => state.values.nameChanges}>
          {(nameChanges) =>
            nameChanges === 'NotAllowed' ? (
              <Alert severity="warning" data-testid="airline-name-changes-forbidden">
                {t('airline.nameChanges.forbiddenInfo')}
              </Alert>
            ) : (
              <div className="gap-phx-3 flex flex-col">
                <form.Field name="nameChangeFeeMode">
                  {(field) => (
                    <SegmentedField<NameChangeFeeMode>
                      field={field}
                      layout="inline"
                      labelWidth={LABEL_W}
                      label={t('airline.nameChangeFeeMode.label')}
                      required
                      helperText={t('airline.nameChangeFeeMode.hint')}
                      options={feeModeOptions}
                      testId="airline-name-change-fee-mode"
                    />
                  )}
                </form.Field>

                <form.Field name="nameChangeDeadlineValue">
                  {(field) => (
                    <TextField
                      field={field}
                      type="number"
                      min={0}
                      layout="inline"
                      labelWidth={LABEL_W}
                      label={t('airline.nameChangeDeadline.label')}
                      helperText={t('airline.nameChangeDeadline.hint')}
                      data-testid="airline-name-change-deadline"
                    />
                  )}
                </form.Field>

                <form.Field name="nameChangeDeadlineUnit">
                  {(field) => (
                    <SegmentedField<DeadlineUnit>
                      field={field}
                      layout="inline"
                      labelWidth={LABEL_W}
                      label={t('airline.nameChangeDeadline.unitLabel')}
                      options={deadlineUnitOptions}
                      testId="airline-name-change-deadline-unit"
                    />
                  )}
                </form.Field>

                <form.Field name="nameChangeOnlineFee">
                  {(field) => (
                    <TextField
                      field={field}
                      type="number"
                      min={0}
                      layout="inline"
                      labelWidth={LABEL_W}
                      label={t('airline.nameChangeOnlineFee.label')}
                      helperText={t('airline.nameChangeFee.hint')}
                      data-testid="airline-name-change-online-fee"
                    />
                  )}
                </form.Field>

                <form.Field name="nameChangeAirportFee">
                  {(field) => (
                    <TextField
                      field={field}
                      type="number"
                      min={0}
                      layout="inline"
                      labelWidth={LABEL_W}
                      label={t('airline.nameChangeAirportFee.label')}
                      data-testid="airline-name-change-airport-fee"
                    />
                  )}
                </form.Field>

                <form.Field name="nameChangeNamelistValue">
                  {(field) => (
                    <TextField
                      field={field}
                      type="number"
                      min={0}
                      layout="inline"
                      labelWidth={LABEL_W}
                      label={t('airline.nameChangeNamelist.label')}
                      helperText={t('airline.nameChangeNamelist.hint')}
                      data-testid="airline-name-change-namelist"
                    />
                  )}
                </form.Field>

                <form.Field name="nameChangeNamelistUnit">
                  {(field) => (
                    <SegmentedField<DeadlineUnit>
                      field={field}
                      layout="inline"
                      labelWidth={LABEL_W}
                      label={t('airline.nameChangeNamelist.unitLabel')}
                      options={deadlineUnitOptions}
                      testId="airline-name-change-namelist-unit"
                    />
                  )}
                </form.Field>

                <form.Field name="nameChangeNote">
                  {(field) => (
                    <TextareaField
                      field={field}
                      layout="inline"
                      labelWidth={LABEL_W}
                      label={t('airline.nameChangeNote.label')}
                      rows={3}
                      data-testid="airline-name-change-note"
                    />
                  )}
                </form.Field>
              </div>
            )
          }
        </form.Subscribe>
      </div>
    </Panel>
  );
}
